import {useSyncExternalStore} from 'react';
import {Kind} from '../types';
import type {FollowUp} from './todos';
import {draftStorage, StoredPhoto} from './draftStorage';
import {draftEntryRoute, draftHasContent, draftLabel, isStale} from './draftState';
import {kindInfo} from './kinds';
import {PreparedPhoto, prepareUpload} from './resizeImage';
import {nowLocal} from './when';

// The tell being written, shared by the three new-tell steps (/tell/new, /kind, /words) and
// kept in draft storage (IndexedDB, photos included) so a reload, a camera round-trip or the
// installed app being closed never loses it. `hydrate()` runs once at app start; the steps wait
// for `ready` before rendering so an empty form never overwrites a stored draft.

export interface DraftPhoto {
    id: string;
    file: File;
    previewUrl: string;
    /** Resizing starts the moment the photo is chosen. */
    prepared: Promise<PreparedPhoto>;
    /** Set when the browser could not read the file; the photo is skipped on save. */
    error: string | null;
}

export interface Draft {
    photos: DraftPhoto[];
    kind: Kind | null;
    detail: string | null;
    text: string;
    plants: string[];
    placeId: string | null;
    whenLocal: string;
    quantity: string;
    happy: boolean;
    followUp: FollowUp | null;
    startedAt: string;
}

const KEY = 'newTell';
const PERSIST_DEBOUNCE_MS = 300;

const fresh = (): Draft => ({photos: [], kind: null, detail: null, text: '', plants: [], placeId: null, whenLocal: nowLocal(), quantity: '', happy: false, followUp: null, startedAt: new Date().toISOString()});

let draft: Draft = fresh();
let ready = false;
let snapshot = {draft, ready};
const listeners = new Set<() => void>();
let persistTimer: number | null = null;

const emit = () => {
    snapshot = {draft, ready};
    listeners.forEach(l => l());
};

const makePhoto = (id: string, file: File): DraftPhoto => {
    const prepared = prepareUpload(file);
    prepared.catch(error => {
        draft = {...draft, photos: draft.photos.map(p => (p.id === id ? {...p, error: error instanceof Error ? error.message : 'Unreadable photo'} : p))};
        emit();
    });
    return {id, file, previewUrl: URL.createObjectURL(file), prepared, error: null};
};

/** Writes the draft through to storage, or removes it when there is nothing worth keeping. */
function persist(immediate = false) {
    if (persistTimer) window.clearTimeout(persistTimer);
    const write = () => {
        persistTimer = null;
        if (!draftHasContent(draft)) {
            void draftStorage.remove(KEY);
            return;
        }
        const {photos, ...fields} = draft;
        void draftStorage.save({
            key: KEY,
            label: draftLabel(draft, k => kindInfo(k).label),
            route: draftEntryRoute(draft),
            listed: true,
            fields,
            photos: photos.filter(p => p.error === null).map<StoredPhoto>(p => ({id: p.id, blob: p.file, name: p.file.name, type: p.file.type})),
            startedAt: draft.startedAt,
            updatedAt: new Date().toISOString()
        });
    };
    if (immediate) write();
    else persistTimer = window.setTimeout(write, PERSIST_DEBOUNCE_MS);
}

export const draftStore = {
    get: () => snapshot,
    /** Loads the stored draft once at app start; drops a stale or empty one. */
    async hydrate() {
        if (ready) return;
        try {
            const record = await draftStorage.load(KEY);
            if (record && !isStale(record.updatedAt)) {
                // Drafts saved before plants became a list carry a single `plant`; keep it.
                const {plant: legacyPlant, ...fields} = record.fields as Partial<Omit<Draft, 'photos'>> & {plant?: string};
                const photos = record.photos.map(p => makePhoto(p.id, p.blob instanceof File ? p.blob : new File([p.blob], p.name, {type: p.type})));
                draft = {...fresh(), ...fields, plants: fields.plants ?? (legacyPlant ? [legacyPlant] : []), photos};
                if (!draftHasContent(draft)) {
                    draft = fresh();
                    void draftStorage.remove(KEY);
                }
            } else if (record) {
                void draftStorage.remove(KEY);
            }
        } catch {
            // storage unavailable: start fresh
        }
        ready = true;
        emit();
    },
    patch(patch: Partial<Omit<Draft, 'photos'>>) {
        draft = {...draft, ...patch};
        emit();
        persist();
    },
    addFiles(files: ArrayLike<File>, max = 6) {
        const room = Math.max(0, max - draft.photos.length);
        const added = Array.from(files)
            .slice(0, room)
            .map(file => makePhoto(crypto.randomUUID(), file));
        if (added.length === 0) return 0;
        draft = {...draft, photos: [...draft.photos, ...added]};
        emit();
        persist(true);
        return added.length;
    },
    /** Hands the readable photos to the uploader (their preview URLs stay alive) and clears the rest. */
    takePhotos(): DraftPhoto[] {
        const taken = draft.photos.filter(p => p.error === null);
        draft.photos.filter(p => p.error !== null).forEach(p => URL.revokeObjectURL(p.previewUrl));
        draft = {...draft, photos: []};
        emit();
        return taken;
    },
    removePhoto(id: string) {
        const photo = draft.photos.find(p => p.id === id);
        if (photo) URL.revokeObjectURL(photo.previewUrl);
        draft = {...draft, photos: draft.photos.filter(p => p.id !== id)};
        emit();
        persist(true);
    },
    reset() {
        if (persistTimer) window.clearTimeout(persistTimer);
        persistTimer = null;
        draft.photos.forEach(p => URL.revokeObjectURL(p.previewUrl));
        draft = fresh();
        void draftStorage.remove(KEY);
        emit();
    },
    subscribe(listener: () => void) {
        listeners.add(listener);
        return () => listeners.delete(listener);
    }
};

/** The draft and whether hydration has finished. */
export const useDraft = () => useSyncExternalStore(draftStore.subscribe, draftStore.get, draftStore.get);
